import { initReveal } from '../common/reveal';

const ALL_CATEGORY = '__all__';

const normalize = (value: string): string =>
    value.trim().toLocaleLowerCase();

const initBlogList = (): void => {
    const root = document.getElementById('blogListPage');
    if (!root) {
        return;
    }

    const list = root.querySelector<HTMLElement>('#blogList');
    const empty = root.querySelector<HTMLElement>('#blogListEmpty');
    const search = root.querySelector<HTMLInputElement>('#blogSearch');
    const category = root.querySelector<HTMLSelectElement>('#blogCategory');
    const sort = root.querySelector<HTMLSelectElement>('#blogSort');
    const count = root.querySelector<HTMLOutputElement>('#blogResultCount');
    const pagination = root.querySelector<HTMLElement>('#blogPagination');

    if (!list || !empty || !search || !category || !sort || !pagination) {
        return;
    }

    const cards = Array.from(list.querySelectorAll<HTMLElement>('.blog-list-card'));
    const countTemplate = count?.dataset.template || '{0}';
    const pageSize = Number(pagination.dataset.pageSize || '12');
    const prevLabel = pagination.dataset.prevLabel || 'Prev';
    const nextLabel = pagination.dataset.nextLabel || 'Next';
    let currentPage = 1;

    const syncQuery = (): void => {
        const params = new URLSearchParams(window.location.search);
        const searchValue = search.value.trim();

        searchValue ? params.set('search', searchValue) : params.delete('search');
        category.value !== ALL_CATEGORY ? params.set('category', category.value) : params.delete('category');
        sort.value !== 'published-desc' ? params.set('sort', sort.value) : params.delete('sort');
        currentPage > 1 ? params.set('page', currentPage.toString()) : params.delete('page');

        const query = params.toString();
        const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
        window.history.replaceState(null, '', nextUrl);
    };

    const applyStateFromQuery = (): void => {
        const params = new URLSearchParams(window.location.search);
        const searchValue = params.get('search') ?? '';
        const categoryValue = params.get('category') ?? ALL_CATEGORY;
        const sortValue = params.get('sort') ?? 'published-desc';
        const pageValue = Number(params.get('page') ?? '1');

        search.value = searchValue;
        if (Array.from(category.options).some(option => option.value === categoryValue)) {
            category.value = categoryValue;
        }
        if (Array.from(sort.options).some(option => option.value === sortValue)) {
            sort.value = sortValue;
        }
        currentPage = Number.isFinite(pageValue) && pageValue > 0 ? Math.floor(pageValue) : 1;
    };

    const sortCards = (items: HTMLElement[]): HTMLElement[] => {
        const mode = sort.value;

        return [...items].sort((a, b) => {
            if (mode === 'title') {
                return (a.dataset.title ?? '').localeCompare(b.dataset.title ?? '', undefined, { sensitivity: 'base' });
            }

            const left = Number(a.dataset.published ?? '0');
            const right = Number(b.dataset.published ?? '0');
            return mode === 'published-asc' ? left - right : right - left;
        });
    };

    const renderPagination = (totalItems: number): void => {
        pagination.innerHTML = '';
        const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

        if (totalPages <= 1) {
            return;
        }

        const fragment = document.createDocumentFragment();

        const addButton = (label: string, page: number, disabled = false, active = false): void => {
            const button = document.createElement('button');
            button.type = 'button';
            button.textContent = label;
            button.dataset.page = page.toString();
            button.disabled = disabled;
            if (active) {
                button.classList.add('is-active');
                button.setAttribute('aria-current', 'page');
            }
            fragment.appendChild(button);
        };

        addButton(prevLabel, Math.max(1, currentPage - 1), currentPage <= 1);

        for (let page = 1; page <= totalPages; page += 1) {
            addButton(page.toString(), page, false, page === currentPage);
        }

        addButton(nextLabel, Math.min(totalPages, currentPage + 1), currentPage >= totalPages);
        pagination.appendChild(fragment);
    };

    const filter = (): void => {
        const term = normalize(search.value);
        const selectedCategory = category.value;

        const ordered = sortCards(cards);
        const matchingCards = ordered.filter(card => {
            const matchesSearch = !term || normalize(card.dataset.search ?? '').includes(term);
            const matchesCategory = selectedCategory === ALL_CATEGORY || card.dataset.category === selectedCategory;
            return matchesSearch && matchesCategory;
        });

        const totalPages = Math.max(1, Math.ceil(matchingCards.length / pageSize));
        currentPage = Math.min(currentPage, totalPages);
        const start = (currentPage - 1) * pageSize;
        const currentCards = new Set(matchingCards.slice(start, start + pageSize));

        ordered.forEach(card => {
            card.hidden = !currentCards.has(card);
            list.appendChild(card);
        });

        empty.hidden = matchingCards.length > 0;
        if (count) {
            count.value = matchingCards.length.toString();
            count.textContent = countTemplate.replace('{0}', matchingCards.length.toString());
        }
        renderPagination(matchingCards.length);
        syncQuery();
    };

    applyStateFromQuery();
    filter();

    const resetAndFilter = (): void => {
        currentPage = 1;
        filter();
    };

    search.addEventListener('input', resetAndFilter);
    category.addEventListener('change', resetAndFilter);
    sort.addEventListener('change', resetAndFilter);
    pagination.addEventListener('click', event => {
        const button = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>('button[data-page]');
        if (!button || button.disabled) {
            return;
        }

        const page = Number(button.dataset.page ?? '1');
        if (!Number.isFinite(page) || page === currentPage) {
            return;
        }

        currentPage = Math.floor(page);
        filter();
        document.getElementById('blog-list')?.scrollIntoView({ block: 'start' });
    });
};

const initBlogPost = (): void => {
    const root = document.getElementById('blogPostPage');
    if (!root) {
        return;
    }

    const progress = document.getElementById('blogProgress');
    const content = root.querySelector<HTMLElement>('#blogContent');
    const tocLinks = Array.from(root.querySelectorAll<HTMLAnchorElement>('#blogToc a'));
    const floatingToc = root.querySelector<HTMLElement>('#blogFloatingToc');
    const tocToggle = root.querySelector<HTMLButtonElement>('.blogpost-toc-toggle');
    const copyLabel = root.dataset.copyLabel || 'Copy';
    const copiedLabel = root.dataset.copiedLabel || 'Copied';

    const updateProgress = (): void => {
        if (!progress) {
            return;
        }

        const total = document.documentElement.scrollHeight - window.innerHeight;
        const current = Math.max(0, window.scrollY);
        const ratio = total > 0 ? Math.min(1, current / total) : 0;
        progress.style.transform = `scaleX(${ratio})`;
    };

    updateProgress();
    window.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress);

    if (content) {
        const blocks = Array.from(content.querySelectorAll<HTMLPreElement>('pre'));
        blocks.forEach(block => {
            const code = block.querySelector('code');
            if (!code || block.classList.contains('code-block')) {
                return;
            }

            block.classList.add('code-block');

            const header = document.createElement('div');
            header.className = 'code-block__header';

            const label = document.createElement('span');
            label.className = 'code-block__label';
            const className = Array.from(code.classList).find(name => name.startsWith('language-'));
            label.textContent = className ? className.replace('language-', '') : 'code';
            header.appendChild(label);

            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'code-block__copy';
            button.textContent = copyLabel;
            button.addEventListener('click', async () => {
                try {
                    await navigator.clipboard.writeText(code.textContent ?? '');
                    button.textContent = copiedLabel;
                    window.setTimeout(() => {
                        button.textContent = copyLabel;
                    }, 1400);
                } catch {
                    button.textContent = copyLabel;
                }
            });
            header.appendChild(button);

            block.insertBefore(header, block.firstChild);
        });
    }

    if (tocLinks.length) {
        const headings = tocLinks
            .map(link => {
                const id = link.getAttribute('href')?.replace('#', '');
                return id ? document.getElementById(id) : null;
            })
            .filter(Boolean) as HTMLElement[];

        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) {
                    return;
                }

                const id = entry.target.getAttribute('id');
                tocLinks.forEach(link => {
                    link.classList.toggle('is-active', link.getAttribute('href') === `#${id}`);
                });
            });
        }, { rootMargin: '0px 0px -72% 0px' });

        headings.forEach(heading => observer.observe(heading));

        tocToggle?.addEventListener('click', () => {
            const nextOpen = !floatingToc?.classList.contains('is-open');
            floatingToc?.classList.toggle('is-open', nextOpen);
            tocToggle.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
        });

        tocLinks.forEach(link => {
            link.addEventListener('click', () => {
                floatingToc?.classList.remove('is-open');
                tocToggle?.setAttribute('aria-expanded', 'false');
            });
        });
    }
};

initReveal({ selector: '#blogPage [data-reveal], #blogListPage [data-reveal], #blogPostPage [data-reveal]', threshold: 0.18 });
initBlogList();
initBlogPost();
